import type { SetI18nParamsFunction } from '#i18n';
import type { ISbResult } from '@storyblok/vue';
import lodashCompact from 'lodash-es/compact';

import { STORYBLOK_HOMEPAGE_STORY_DEFAULT_FULL_SLUG } from '#shared/constants/storyblok';
import { defaultLocale } from '~i18n/i18nConfig';

export type UseStoryblokSeoMetaReturnType = void;

export const useStoryblokSeoMeta = (story: MaybeRefOrGetter<ISbResult['data']['story']>): UseStoryblokSeoMetaReturnType => {
    // COMPOSABLES
    const setI18nParams = useSetI18nParams();

    // DATA
    const translatedSlugs = computed(() => [{ lang: defaultLocale }, ...(toValue(story)?.translated_slugs ?? [])]);
    const blok = computed<ContentType>(() => toValue(story).content);
    const title = computed(() => blok.value.seo?.title || toValue(story)?.title || '');

    // META
    setI18nParams(
        translatedSlugs.value.reduce(
            (acc: Parameters<SetI18nParamsFunction>[0], slug: StoryblokTranslatedSlug): Parameters<SetI18nParamsFunction>[0] => {
                if (story.value.default_full_slug === STORYBLOK_HOMEPAGE_STORY_DEFAULT_FULL_SLUG) {
                    return acc;
                }

                if (slug.published) {
                    acc[slug.lang] = { slug: lodashCompact(slug.path.split('/')) };
                } else {
                    acc[slug.lang] = { slug: lodashCompact(story.value.default_full_slug.split('/')) };
                }

                return acc;
            },
            {}
        )
    );

    useSeoMeta({
        title: () => title.value,
        ...setIf(!!blok.value.seo?.description, () => ({
            description: blok.value.seo?.description,
            ogDescription: blok.value.seo?.['og:description'] || blok.value.seo?.description,
            twitterDescription: blok.value.seo?.['twitter:description'] || blok.value.seo?.description,
        })),
        ogTitle: blok.value.seo?.['og:title'] || title.value,
        ...setIf(!!blok.value.seo?.['og:image'], () => ({
            ogImageUrl: blok.value.seo?.['og:image'],
            ogImageAlt: title.value,
        })),
        ...setIf(!!blok.value.seo?.['og:image'], () => ({
            ogImage: {
                url: blok.value.seo?.['og:image'],
                alt: blok.value.seo?.['og:title'] || title.value,
            },
        })),
        twitterTitle: blok.value.seo?.['twitter:title'] || blok.value.seo?.['og:title'] || title.value,
        ...setIf(!!blok.value.seo?.['twitter:card'], () => ({
            twitterCard: blok.value.seo?.['twitter:card'],
        })),
        ...setIf(!!blok.value.seo?.['twitter:image'], () => ({
            twitterImage: blok.value.seo?.['twitter:image'],
            twitterImageAlt: title.value,
        })),
    });
};
